import { bind, Component, createRef, h, signal, tag } from "omi";
import classNames from "classnames";
import isEmpty from "lodash-es/isEmpty";

import { NWButton } from "@/wc/components/atoms/nw-button";
import { NWError } from "@/wc/components/base/nw-error";
import { NWSuspense } from "@/wc/components/utility/nw-suspense";
import { ContentTypeFilter } from "@/wc/pages/project/features/directory/components/contentTypeFilter";
import { DirectoryStore } from "@/wc/pages/project/features/directory/store";
import { toDTOType } from "@/wc/pages/project/features/directory/utils";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import { installMadeForYou } from "../../features/madeForYou/install";
import { useSearch } from "../../store/queries/useSearch";
import { DirectorySearchBar } from "../../components/searchBar";
import { SearchList } from "../../components/searchList";
import { TopicButton } from "../../components/topicList";
import { DirectoryView } from "../../views";
import { NWDirectoryCourseView, type CourseSearchResult } from "./courseView";

type Props = Record<string, never>;

@tag("nw-directory-explore")
export class NWDirectoryExploreView extends Component<Props> {
  static css = [tailwind];

  private search = useSearch();
  private scrollContainerRef = createRef<HTMLDivElement>();

  private activeTypeFilter = signal<string>("All");

  install() {
    // Initialize search with "All" filter (empty query, no type filter)
    this.search.fetch("", undefined);
  }

  private handleTypeFilterChange = (type: string) => {
    this.activeTypeFilter.value = type;

    const backendType = toDTOType(type);
    const currentQuery = this.search.searchQuery.value || "";
    this.search.fetch(currentQuery, backendType);
  };

  private handleSearchChange = (query: string) => {
    if (!isEmpty(DirectoryStore.viewingCourse.value)) {
      DirectoryStore.setViewingCourse(undefined);
    }

    // Reset course type filter to "All" when search term is cleared
    if (isEmpty(query) && this.activeTypeFilter.value !== "All") {
      this.activeTypeFilter.value = "All";
    }

    const backendType = toDTOType(this.activeTypeFilter.value);
    this.search.setSearchQuery(query, backendType);

    setTimeout(() => {
      if (this.scrollContainerRef.current) {
        this.scrollContainerRef.current.scrollTop = 0;
      }
    }, 0);
  };

  installed() {
    this.scrollContainerRef.current?.addEventListener("wheel", this.handleOverscroll);
  }

  uninstall() {
    this.scrollContainerRef.current?.removeEventListener("wheel", this.handleOverscroll);
  }

  @bind
  private handleOverscroll(event: WheelEvent) {
    if (!this.scrollContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = this.scrollContainerRef.current!;
    if ((event.deltaY < 0 && scrollTop === 0) || (event.deltaY > 0 && scrollTop + clientHeight >= scrollHeight)) {
      event.preventDefault();
    }
  }

  private onSelectCourse = (course: CourseSearchResult) => {
    const title = course.title;

    if (!isEmpty(title)) {
      DirectoryStore.setViewingCourse(title);

      import("@/services/analyticsService").then(({ useAnalyticsService }) => {
        useAnalyticsService().trackSeriesViewed(title);
      });
    }
  };

  private toggleExpand = () => {
    DirectoryStore.recommendedExpanded.value = !DirectoryStore.recommendedExpanded.value;
  };

  private getActiveCourse() {
    if (isEmpty(DirectoryStore.viewingCourse.value)) {
      return undefined;
    }

    if (this.search.data.value) {
      const course = this.search.data.value.find(
        r => r.type === "course" && r.title === DirectoryStore.viewingCourse.value,
      );

      if (course) {
        return course;
      }
    }

    return undefined;
  }

  private getCourseSearchResults(showMinizedView: boolean = false) {
    const courses = (this.search.data.value || []).filter(r => r.type === "course");

    if (showMinizedView) {
      return courses.slice(0, DirectoryStore.recommendedExpanded.value ? undefined : 3);
    }

    return courses;
  }

  private handleBack = () => {
    // If the search query is a series search, clear the search query to show the explore view
    if (useSearch().isPrefixSearch("series:")) {
      DirectoryStore.search("");
    }

    DirectoryStore.setViewingCourse(undefined);
  };

  renderOnboardingView() {
    const allError = this.search.error.value;
    const hasSearchTerm = !isEmpty(this.search.searchQuery.value);
    const activeCourse = this.getActiveCourse();
    const showMinizedView = Store.app.route.value === "onboarding";
    const courses = this.getCourseSearchResults(showMinizedView);

    if (DirectoryStore.activeView.value === DirectoryView.MADE_FOR_YOU) {
      return (
        <div className="flex flex-col space-y-10 mt-6 overflow-y-auto pr-2 h-full" ref={this.scrollContainerRef}>
          <NWSuspense loadFunc={installMadeForYou} onLoadComplete={({ MadeForYou }) => <MadeForYou />} />
        </div>
      );
    }

    return (
      <div
        className="flex flex-col space-y-2.5 overflow-y-auto pb-6 pr-2 h-full min-h-80"
        ref={this.scrollContainerRef}
      >
        <div class="sticky top-0 z-10">
          <DirectorySearchBar
            query={this.search.searchQuery.value}
            onChange={this.handleSearchChange}
            focusOnInstall={true}
          />
        </div>

        {allError && (
          <div className="animate-fadeInFast">
            <NWError />
          </div>
        )}

        {!allError && !activeCourse && (
          <>
            <div className={classNames("flex flex-col space-y-4 animate-fadeInFast", { hidden: hasSearchTerm })}>
              <div className="flex justify-between items-center">
                <div className="flex-shrink-0">
                  <ContentTypeFilter
                    activeTypeFilter={this.activeTypeFilter.value}
                    handleTypeFilterChange={this.handleTypeFilterChange}
                  />
                </div>
                {showMinizedView && (
                  <NWButton size="fit" type="text" onClick={this.toggleExpand}>
                    <span className="font-normal">
                      {DirectoryStore.recommendedExpanded.value ? "Show less" : "Show more"}
                    </span>
                  </NWButton>
                )}
              </div>
              {this.search.isLoading.value ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="h-52 bg-gray-200 animate-pulse rounded-2xl"></div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {courses.map(course => (
                    <TopicButton
                      key={course.title}
                      searchResult={course}
                      onClick={() => this.onSelectCourse(course as CourseSearchResult)}
                    />
                  ))}
                </div>
              )}
            </div>

            {hasSearchTerm && (
              <div className={classNames("flex flex-col space-y-4 pb-8")}>
                <div className="flex-shrink-0">
                  <ContentTypeFilter
                    activeTypeFilter={this.activeTypeFilter.value}
                    handleTypeFilterChange={this.handleTypeFilterChange}
                  />
                </div>
                <div className="animate-fadeInFast">
                  <SearchList
                    results={this.search.data.value || []}
                    isLoading={this.search.isLoading.value}
                    error={this.search.error.value}
                    hasSearchTerm={true}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {!allError && activeCourse && (
          <NWDirectoryCourseView
            searchResult={activeCourse as CourseSearchResult}
            recommendedResult={undefined}
            onBack={this.handleBack}
          />
        )}
      </div>
    );
  }

  render() {
    const allError = this.search.error.value;
    const hasSearchTerm = !isEmpty(this.search.searchQuery.value);
    const activeCourse = this.getActiveCourse();
    const courses = this.getCourseSearchResults();

    if (DirectoryStore.isOnboardingModeActive()) {
      return this.renderOnboardingView();
    }

    if (DirectoryStore.activeView.value === DirectoryView.MADE_FOR_YOU) {
      return (
        <div className="flex flex-col space-y-10 mt-6 overflow-y-auto pr-2 h-full" ref={this.scrollContainerRef}>
          <NWSuspense loadFunc={installMadeForYou} onLoadComplete={({ MadeForYou }) => <MadeForYou />} />
        </div>
      );
    }

    return (
      <div className="flex flex-col mt-4 overflow-y-auto pr-2 h-full flex-1 min-h-0">
        {/* Search Bar */}
        <div class="sticky top-0 z-20 bg-white pb-2.5">
          <DirectorySearchBar query={this.search.searchQuery.value} onChange={this.handleSearchChange} />
        </div>

        <div className="flex-shrink-0 mb-1">
          <ContentTypeFilter
            activeTypeFilter={this.activeTypeFilter.value}
            handleTypeFilterChange={this.handleTypeFilterChange}
          />
        </div>

        {/* Error State */}
        {allError && (
          <div className="animate-fadeInFast">
            <NWError />
          </div>
        )}

        {/* Content */}
        {!allError && !activeCourse && (
          <>
            {/* Show search results when there's a search term or type filter is active */}
            {(hasSearchTerm || this.activeTypeFilter.value !== "All") && (
              <div className="animate-fadeInFast">
                <SearchList
                  results={this.search.data.value || []}
                  isLoading={this.search.isLoading.value}
                  error={this.search.error.value}
                  hasSearchTerm={hasSearchTerm || this.activeTypeFilter.value !== "All"}
                />
              </div>
            )}

            {/* Show filtered series when no search term and "All" filter */}
            {!hasSearchTerm &&
              this.activeTypeFilter.value === "All" &&
              (this.search.isLoading.value ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 pb-8">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="h-52 bg-gray-200 animate-pulse rounded-2xl"></div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 pb-8">
                  {courses.map(course => (
                    <TopicButton
                      key={course.title}
                      searchResult={course}
                      onClick={() => this.onSelectCourse(course as CourseSearchResult)}
                    />
                  ))}
                  {courses.length === 0 && (
                    <div className="col-span-full text-center text-tertiary py-10">No results found.</div>
                  )}
                </div>
              ))}
          </>
        )}

        {/* Active Course View */}
        {!allError && activeCourse && (
          <NWDirectoryCourseView
            searchResult={activeCourse as CourseSearchResult}
            recommendedResult={undefined}
            onBack={this.handleBack}
          />
        )}
      </div>
    );
  }
}
